import React, { useEffect, useRef, useState } from "react";

export default function FancySelectNativeLook({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [dropdownUp, setDropdownUp] = useState(false);
  const ref = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      setOpen(false);
      setOpenDescriptionFor(null);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);

    const handleKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setOpenDescriptionFor(null);
      }
    };
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  // Detectar si hay espacio hacia abajo para mostrar dropdown
  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = Math.min(240, options.length * 40 + 20); // max-h-60 ≈ 240px

    setDropdownUp(spaceBelow < dropdownHeight + 10); // +10 para margen
  }, [open, options.length]);

  const [openDescriptionFor, setOpenDescriptionFor] = useState(null);
  const selected = options.find((opt) => String(opt.value) === String(value));
  const selectedLabel = selected?.label || "";

  return (
    <div className={`relative w-full min-w-0 overflow-visible ${className}`} ref={ref}>
      {label && (
        <label className="text-[12px] text-[#686873] block pb-1">{label}</label>
      )}

      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onPointerDown={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
          setOpenDescriptionFor(null);
          setOpen((p) => !p);
        }}
        className={`
          w-full relative
          rounded-xl border border-slate-200 bg-slate-50
          mt-3
          px-3 py-2 pr-10
          text-left text-[14px] text-slate-900
          shadow-sm
          outline-none
          ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-slate-300"}
        `}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`block truncate ${selectedLabel ? "" : "text-[#A7A7A7]"}`}>
          {selectedLabel || placeholder}
        </span>

        <span
          className={`
            pointer-events-none
            absolute right-3 top-4 -translate-y-1/2
            w-2 h-2 border border-[#686873] border-t-0 border-l-0 rotate-45
            transition-transform
            ${open ? "rotate-[225deg] translate-y-[-30%]" : ""}
          `}
        />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: dropdownUp ? "auto" : "100%",
            bottom: dropdownUp ? "100%" : "auto",
            left: 0,
            right: 0,
          }}
          className="bg-white border border-[#F2F2F2] rounded-lg shadow-lg z-[9999] max-h-60 overflow-y-auto mt-1"
        >
          {options.length === 0 && (
            <div className="px-4 py-2 text-[14px] text-[#98999A]">
              No options available
            </div>
          )}

          {options.map((opt) => {
            const isSelected = String(value) === String(opt.value);
            const isDescriptionOpen = openDescriptionFor === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                title={opt.description || undefined}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(opt.value);
                  setOpen(false);
                  setOpenDescriptionFor(null);
                }}
                className={`
                  w-full text-left
                  min-h-[4rem]
                  px-4 py-3
                  ${isSelected ? "bg-[#F0F5F0] text-[#0F0F0F]" : "text-[#101010]"}
                  hover:bg-[#F7F9FA]
                `}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="block max-w-[calc(100%-3rem)] whitespace-normal break-words font-medium leading-5">
                    {opt.label}
                  </span>
                  {opt.description ? (
                            <span
                      role="button"
                      tabIndex={0}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenDescriptionFor((prev) =>
                          prev === opt.value ? null : opt.value,
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setOpenDescriptionFor((prev) =>
                            prev === opt.value ? null : opt.value,
                          );
                        }
                      }}
                      className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100"
                      aria-label={isDescriptionOpen ? "Close description" : "Open description"}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                      </svg>
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {openDescriptionFor ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-[10000] mx-auto w-full max-w-xl px-4 py-4 md:px-0">
          <div className="pointer-events-auto relative rounded-xl border border-slate-200 bg-white/95 p-4 text-sm text-slate-700 shadow-xl backdrop-blur-xl">
            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpenDescriptionFor(null);
              }}
              className="absolute right-3 top-1 text-xl font-bold text-slate-500 hover:text-slate-700"
              aria-label="Close description"
            >
              x
            </button>
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Option info
                </p>
                <p className="mt-1 text-base font-semibold text-slate-900 whitespace-normal break-words leading-6">
                  {options.find((opt) => String(opt.value) === String(openDescriptionFor))?.label}
                </p>
              </div>
            </div>
            <p className="mt-3 whitespace-normal break-words leading-6 text-slate-600">
              {options.find((opt) => String(opt.value) === String(openDescriptionFor))?.description}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}